import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import { store } from '@/store';
import Layout from '@/components/Layout/Layout';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Slider from '@/pages/Slider';
import ProtectedRoute from '@/components/Auth/ProtectedRoute';
import { setToken } from '@/features/auth/authSlice';

// Import Tailwind CSS
import './index.css';
import '@/assets/css/style.css';
import 'react-toastify/dist/ReactToastify.css';

import SliderForm from './components/AdSlider/AdSlider';
import Content from './pages/Content';
import OurFaculty from './pages/OurFaculty';
import Testimonial from './pages/Testimonial';
import YoutubeChannels from './pages/YoutubeChannels';
import YoutubeVideos from './pages/YoutubeVideos';
import ContentFeature from './components/AdSlider/AddContent';
import YoutubeChannelForm from './components/YoutubeChannel/AddYoutubeChannel';
import VideosForm from './components/AddVideos/AddVideos';
import { ToastContainer } from 'react-toastify';
import FacultyForm from './components/AddFaculty/AddFaculty';
import AboutDetails from './components/AboutUs/AboutDetails';
import ContactDetails from './components/ContactUs/ContactUs';
import ContentForm from './components/Content/ContentForm';
import Courses from './pages/Courses';
import CourseForm from './components/Course/CourseForm';
// import { Tags } from 'lucide-react';
import Tags from './pages/Tags';
import TagForm from './components/Tags/TagsForm';
import Categories from './pages/Categories';
import CategoryForm from './components/categories/CategoryForm';
import Blogs from './pages/Blogs';
import BlogForm from './components/Blogs/BlogForm';
import ProductList from './pages/ProductList';
import ProductsForm from './components/Products/ProductsForm';
import ViewProduct from './components/Products/ViewProduct';
import CourseList from './pages/CourseList';
import CoursesForm from './components/Courses/CoursesForm';
import Onboarding from './pages/Onboarding';
import HeaderPage from './pages/HeaderPage';
import FooterPage from './pages/FooterPage';
import EditFooter from './components/Footer/EditFooter';
import AddFooter from './components/Footer/AddFooter';
import EditFooterApp from './components/Footer/EditFooterApp';
import Category from './pages/Category';
import AddCategory from './components/category/AddCategory';
import EditCategory from './components/category/EditCategory';
import SubCategory from './pages/SubCategory';
import AddSubCategory from './components/SubCategory/AddSubCategory';
import EditSubCategory from './components/SubCategory/EditSubCategory';
import SEOPage from './pages/SEOPage';
import AddSEO from './components/SEO/AddSEO';
import Books from './pages/Books';
import BookForm from './components/Books/BookForm';


const AppRoutes: React.FC = () => {
  const dispatch = useDispatch();

  // Restore token on page load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      dispatch(setToken(token));
    }
  }, [dispatch]);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Router>
        <div className="App">
          <Toaster position="top-right" />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Layout hideChrome><Login /></Layout>} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            <Route path="/onboarding" element={<Layout hideChrome><Onboarding /></Layout>} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout><Dashboard /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/slider"
              element={
                <ProtectedRoute>
                  <Layout><Slider /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/content"
              element={
                <ProtectedRoute>
                  <Layout><Content /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/our-faculty"
              element={
                <ProtectedRoute>
                  <Layout><OurFaculty /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/our-faculty/add"
              element={
                <ProtectedRoute>
                  <Layout><FacultyForm /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/our-faculty/edit/:id"
              element={
                <ProtectedRoute>
                  <Layout><FacultyForm /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/testimonial"
              element={
                <ProtectedRoute>
                  <Layout><Testimonial /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/youtube-channels"
              element={
                <ProtectedRoute>
                  <Layout><YoutubeChannels /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/youtube-channels/add"
              element={
                <ProtectedRoute>
                  <Layout><YoutubeChannelForm /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/youtube-channels/edit/:id"
              element={
                <ProtectedRoute>
                  <Layout><YoutubeChannelForm /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/youtube-videos/add"
              element={
                <ProtectedRoute>
                  <Layout><VideosForm /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/youtube-videos/edit/:id"
              element={
                <ProtectedRoute>
                  <Layout><VideosForm /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/youtube-videos"
              element={
                <ProtectedRoute>
                  <Layout><YoutubeVideos /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/content-feature/add/:contentId"
              element={
                <ProtectedRoute>
                  <Layout><ContentFeature /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/content-feature/edit/:id"
              element={
                <ProtectedRoute>
                  <Layout><ContentFeature /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/content/edit/:id"
              element={
                <ProtectedRoute>
                  <Layout><ContentForm /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sliders/add"
              element={
                <ProtectedRoute>
                  <Layout><SliderForm /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sliders/edit/:id"
              element={
                <ProtectedRoute>
                  <Layout><SliderForm /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/about-details'
              element={
                <ProtectedRoute>
                  <Layout><AboutDetails /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/contact-details'
              element={
                <ProtectedRoute>
                  <Layout><ContactDetails /></Layout>
                </ProtectedRoute>
              }
            />

            <Route path='/blogs/tags' element={
              <ProtectedRoute>
                <Layout><Tags /></Layout>
              </ProtectedRoute>
            } />

            <Route path='/blogs/tags/add' element={
              <ProtectedRoute>
                <Layout><TagForm /></Layout>
              </ProtectedRoute>
            } />
            <Route path='/blogs/tags/edit/:id' element={
              <ProtectedRoute>
                <Layout><TagForm /></Layout>
              </ProtectedRoute>
            } />

            <Route path='/blogs/categories' element={
              <ProtectedRoute>
                <Layout><Categories /></Layout>
              </ProtectedRoute>
            } />
            <Route path='/blogs/categories/add' element={
              <ProtectedRoute>
                <Layout><CategoryForm /></Layout>
              </ProtectedRoute>
            } />

            <Route path='/blogs/categories/edit/:id' element={
              <ProtectedRoute>
                <Layout><CategoryForm /></Layout>
              </ProtectedRoute>
            } />

            <Route path='/blogs/tags/add' element={
              <ProtectedRoute>
                <Layout><TagForm /></Layout>
              </ProtectedRoute>
            } />
            <Route path='/blogs/tags/edit/:id' element={
              <ProtectedRoute>
                <Layout><TagForm /></Layout>
              </ProtectedRoute>
            } />

            <Route path='/blogs/categories' element={
              <ProtectedRoute>
                <Layout><Categories /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/blogs" element={
              <ProtectedRoute>
                <Layout><Blogs /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/blogs/add" element={
              <ProtectedRoute>
                <Layout><BlogForm /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/blogs/edit/:id" element={
              <ProtectedRoute>
                <Layout><BlogForm /></Layout>
              </ProtectedRoute>
            } />

            {/* Courses routes */}
            <Route
              path="/courses"
              element={
                <ProtectedRoute>
                  <Layout><Courses /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/courses/add"
              element={
                <ProtectedRoute>
                  <Layout><CourseForm /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/courses/edit/:id"
              element={
                <ProtectedRoute>
                  <Layout><CourseForm /></Layout>
                </ProtectedRoute>
              }
            />


                 {/* Courses routes */}
            <Route
              path="/books"
              element={
                <ProtectedRoute>
                  <Layout><Books /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/books/add"
              element={
                <ProtectedRoute>
                  <Layout><BookForm /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/books/edit/:id"
              element={
                <ProtectedRoute>
                  <Layout><BookForm /></Layout>
                </ProtectedRoute>
              }
            />


            <Route path="/products/list" element={
              <ProtectedRoute>
                <Layout><ProductList /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/products/add" element={
              <ProtectedRoute>
                <Layout><ProductsForm /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/products/edit/:id" element={
              <ProtectedRoute>
                <Layout><ProductsForm /></Layout>
              </ProtectedRoute>
            } />
            <Route path='/products/view/:id' element={
              <ProtectedRoute>
                <Layout><ViewProduct /></Layout>
              </ProtectedRoute>
            } />

            <Route path='/courses/list' element={
              <ProtectedRoute>
                <Layout><CourseList /></Layout>
              </ProtectedRoute>
            } />
            <Route path='/courses/add' element={
              <ProtectedRoute>
                <Layout><CourseForm /></Layout>
              </ProtectedRoute>
            } />
            <Route path='/courses/edit/:id' element={
              <ProtectedRoute>
                <Layout><ProductsForm /></Layout>
              </ProtectedRoute>
            } />

            {/*Setting*/}

            <Route
  path="/settings/header"
  element={
    <ProtectedRoute>
      <Layout><HeaderPage/></Layout>
    </ProtectedRoute>
  }
/>

           <Route
  path="/settings/footer"
  element={
    <ProtectedRoute>
      <Layout><FooterPage/></Layout>
    </ProtectedRoute>
  }
/>
         <Route
  path="/settings/footer/edit/:id"
  element={
    <ProtectedRoute>
      <Layout><EditFooter/></Layout>
    </ProtectedRoute>
  }
/>

<Route
  path="/settings/footer-app/add"
  element={
    <ProtectedRoute>
      <Layout><AddFooter /></Layout>
    </ProtectedRoute>
  }
/>

<Route
  path="/settings/footer-app/edit/:id"
  element={
    <ProtectedRoute>
      <Layout><EditFooterApp /></Layout>
    </ProtectedRoute>
  }
/>

<Route
  path="/settings/category"
  element={
    <ProtectedRoute>
      <Layout><Category /></Layout>
    </ProtectedRoute>
  }
/>

<Route
  path="/settings/category/add-category"
  element={
    <ProtectedRoute>
      <Layout><AddCategory /></Layout>
    </ProtectedRoute>
  }
/>
<Route
  path="/settings/category/edit/:id"
  element={
    <ProtectedRoute>
      <Layout><EditCategory /></Layout>
    </ProtectedRoute>
  }
/>
 <Route
  path="/settings/sub-category"
  element={
    <ProtectedRoute>
      <Layout><SubCategory /></Layout>
    </ProtectedRoute>
  }
/>

<Route
  path="/settings/subcategory/add"
  element={
    <ProtectedRoute>
      <Layout><AddSubCategory /></Layout>
    </ProtectedRoute>
  }
/>
<Route
  path="/settings/subcategory/edit/:id"
  element={
    <ProtectedRoute>
      <Layout><EditSubCategory /></Layout>
    </ProtectedRoute>
  }
/>

<Route
  path="/settings/seo"
  element={
    <ProtectedRoute>
      <Layout><SEOPage /></Layout>
    </ProtectedRoute>
  }
/>

<Route
  path="/settings/seo/add"
  element={
    <ProtectedRoute>
      <Layout><AddSEO /></Layout>
    </ProtectedRoute>
  }
/>

<Route
  path="/settings/seo/edit/:id"
  element={
    <ProtectedRoute>
      <Layout><EditSubCategory /></Layout>
    </ProtectedRoute>
  }
/>





            {/* Catch all */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </Router>
    </>
  );
};

const App: React.FC = () => (
  <Provider store={store}>
    <AppRoutes />
  </Provider>
);

export default App;
